import { useEffect, useState } from 'react';
import {
  atualizarEtapaCessao,
  excluirArquivoTitulosCessao,
  getTitulosSemDocumentos,
  listarArquivosTitulosCessao,
  uploadArquivosTitulosCessao,
  validaTitulosCedidos
} from 'api/capitalFlow';
import { formatarDocumento } from 'components/Utils/documentoAlfanumerico';
import { openSnackbarMain } from 'components/Shared/openSnackbarMain';
import sanitizeFileName from 'utils/sanitizeFileName';

const useUploadCessaoHook = ({ setCarregarTela }) => {
  const [openModalTitulosCedidos, setOpenModalTitulosCedidos] = useState(false);
  const [mostrarComponenteUpload, setMostrarComponenteUpload] = useState(false);
  const [liberarBtnProsseguir, setLiberarBtnProsseguir] = useState(false);
  const [mostrarEnvioTitulos, setMostrarEnvioTitulos] = useState(false);
  const [loadingProsseguir, setLoadingProsseguir] = useState(false);
  const [openModalCancelar, setOpenModalCancelar] = useState(false);
  const [arquivosEnviados, setArquivosEnviados] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [arquivosUI, setArquivosUI] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [documentosPendencias, setDocumentosPendencias] = useState([]);

  const tiposPendencias = {
    SEM_DOCUMENTO_ASSINADO: {
      grid: 'Titulo sem documento assinado',
      titulo: 'Existem títulos sem o documento assinado.',
      documento: 'documento assinado'
    },
    SEM_ADITIVO_ASSINADO: {
      grid: 'Titulo sem aditivo assinado',
      titulo: 'Existem títulos sem o aditivo assinado.',
      documento: 'aditivo assinado'
    },
    SEM_XML_DM: {
      grid: 'Titulo sem XML atrelado',
      titulo: 'Existem títulos sem o XML da duplicata.',
      documento: 'XML da duplicata'
    },
    SEM_DOCUMENTO_NOTA_SERVICO: {
      grid: 'Titulo sem nota de serviço incluida',
      titulo: 'Existem títulos sem a nota de serviço incluída.',
      documento: 'nota de serviço'
    },
    SEM_DOCUMENTO_CONTRATO_FISICO: {
      grid: 'Titulo sem contrato físico incluido',
      titulo: 'Existem títulos sem o contrato físico incluído.',
      documento: 'contrato físico'
    }
  };

  useEffect(() => {
    if (mostrarEnvioTitulos) {
      buscarTitulosSemDocumentos();
    }
  }, [mostrarEnvioTitulos]);

  const voltarParaSimulacao = () => {
    setCarregarTela('NovaCessao');
  };

  const buildDocumentosPendencias = titulos => {
    if (!Array.isArray(titulos) || titulos.length <= 0) return [];
    return [
      ...new Set(
        titulos.flatMap(t =>
          t.PENDECIASDOCUMENTOS && Array.isArray(t.PENDECIASDOCUMENTOS) ? t.PENDECIASDOCUMENTOS : []
        )
      )
    ];
  };

  const appendFormDataValue = (formData, chave, valor) => {
    if (valor === undefined || valor === null) return;

    if (Array.isArray(valor)) {
      valor.forEach((item, index) => {
        appendFormDataValue(formData, `${chave}[${index}]`, item);
      });
      return;
    }

    if (typeof valor === 'object' && !(valor instanceof File) && !(valor instanceof Blob)) {
      Object.entries(valor).forEach(([subChave, subValor]) => {
        appendFormDataValue(formData, `${chave}[${subChave}]`, subValor);
      });
      return;
    }

    formData.append(chave, valor);
  };

  const buscarTitulosSemDocumentos = () => {
    setLoading(true);
    getTitulosSemDocumentos()
      .then(res => {
        const dados = res.data?.['data'] ?? [];
        setRows(dados);
        setDocumentosPendencias(buildDocumentosPendencias(dados));
        buscarArquivosEnviados();
        setMostrarComponenteUpload(dados.length > 0);
        setLiberarBtnProsseguir(dados.length <= 0);
      })
      .catch(err => {
        openSnackbarMain('Ocorreu um erro ao buscar os títulos sem documentos', 'error');
        console.error('Erro ao buscar os títulos sem documentos', err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const buscarArquivosEnviados = () => {
    listarArquivosTitulosCessao()
      .then(res => {
        const dados = res.data?.data ?? res.data ?? [];
        setArquivosEnviados(dados);
      })
      .catch(err => {
        openSnackbarMain('Ocorreu um erro ao listar arquivos', 'error');
        console.error('Erro ao listar arquivos enviados', err);
      });
  };

  const onUploadFiles = async (files, onProgress) => {
    if (!files?.length) return;

    const allowed = ['pdf', 'zip', 'xml'];
    const invalid = files.find(f => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      return !allowed.includes(ext);
    });

    if (invalid) {
      console.warn('Arquivo inválido:', invalid.name);
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const formData = new FormData();
      files.forEach(file => {
        const sanitizedName = sanitizeFileName(file.name);
        const sanitizedFile = new File([file], sanitizedName, {
          type: file.type,
          lastModified: file.lastModified
        });
        formData.append('arquivos[]', sanitizedFile);
      });
      appendFormDataValue(formData, 'titulos', rows ?? []);

      await uploadArquivosTitulosCessao(formData, e => {
        const total = e.total ?? 0;
        const percent = total ? Math.round((e.loaded * 100) / total) : 0;
        setUploadProgress(percent);
        onProgress?.(percent);
      });

      await buscarArquivosEnviados();
    } catch (err) {
      openSnackbarMain('Ocorreu um erro ao enviar arquivos', 'error');
      console.error('Erro ao enviar arquivos', err);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const excluirArquivoEnviado = async ({ UUID }) => {
    try {
      await excluirArquivoTitulosCessao(UUID);
    } catch (err) {
      openSnackbarMain('Ocorreu um erro ao excluir arquivo', 'error');
      throw err;
    }
  };

  const irParaTelaContaCorrente = () => {
    setLoadingProsseguir(true);
    const novaEtapa = 'ContaCorrenteCessao';
    atualizarEtapaCessao({ etapa: novaEtapa })
      .then(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setCarregarTela(novaEtapa);
      })
      .catch(err => {
        openSnackbarMain('Ocorreu um erro ao atualizar a etapa da cessão', 'error');
        console.error('Erro ao atualizar a etapa da cessão:', err);
      })
      .finally(() => setLoadingProsseguir(false));
  };

  const fmtBRDate = iso => {
    const d = parseISODateLocal(iso);
    return d ? d.toLocaleDateString('pt-BR') : '';
  };

  const parseISODateLocal = s => {
    if (!s) return null;
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const d = new Date(+m[1], +m[2] - 1, +m[3], 0, 0, 0, 0);
      return isNaN(d) ? null : d;
    }
    const d = new Date(s);
    return isNaN(d) ? null : d;
  };

  const formataValor = valor =>
    parseFloat(valor).toLocaleString('pt-br', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

  const formataLabelsPendenciasDocumentos = pendencias => {
    if (!pendencias || !Array.isArray(pendencias)) return '--';
    return pendencias.map(p => tiposPendencias[p]?.grid || p).join(', ');
  };

  const onCloseModalCancelar = () => {
    setOpenModalCancelar(false);
  };

  const handleUploadedFilesChange = files => {
    setArquivosUI(files);
    const temArquivoOk = files.some(f => f.status === 'done');
    setLiberarBtnProsseguir(temArquivoOk);
  };

  const columns = [
    {
      field: 'NOMESACADO',
      headerName: 'Sacado',
      flex: 1.5,
      minWidth: 250,
      valueGetter: ({ row }) => `${formatarDocumento(row.CPFCNPJSACADO)} - ${row.NOMESACADO}`
    },
    {
      field: 'NOMECEDENTE',
      headerName: 'Cedente',
      flex: 1.5,
      minWidth: 250,
      valueGetter: ({ row }) => `${formatarDocumento(row.CPFCNPJCEDENTE)} - ${row.NOMECEDENTE}`
    },
    { field: 'NBORDERO', headerName: 'Borderô', flex: 0.7, minWidth: 80 },
    { field: 'NDOC', headerName: 'Nº Doc.', flex: 0.8, minWidth: 100 },
    {
      field: 'VENCIMENTO',
      headerName: 'Vencimento',
      flex: 1.0,
      minWidth: 90,
      valueFormatter: ({ value }) => fmtBRDate(value)
    },
    {
      field: 'VALOR',
      headerName: 'Valor (R$)',
      flex: 1.0,
      minWidth: 120,
      valueFormatter: ({ value }) => formataValor(value)
    },
    {
      field: 'PENDECIASDOCUMENTOS',
      headerName: 'Pendências',
      flex: 1.0,
      minWidth: 130,
      valueGetter: ({ row }) => formataLabelsPendenciasDocumentos(row.PENDECIASDOCUMENTOS)
    }
  ];

  const verificaTitulosCedidos = () => {
    setLoadingProsseguir(true);
    validaTitulosCedidos()
      .then(response => {
        if (response.data?.existe_conflito) {
          setOpenModalTitulosCedidos(true);
          setLoadingProsseguir(false);
        } else {
          irParaTelaContaCorrente();
        }
      })
      .catch(() => {
        setLoadingProsseguir(false);
        openSnackbarMain('Ocorreu um erro ao validar os títulos cedidos', 'error');
      });
  };

  const onCloseModalTitulosCedidos = () => {
    setOpenModalTitulosCedidos(false);
  };

  return {
    onCloseModalTitulosCedidos,
    handleUploadedFilesChange,
    irParaTelaContaCorrente,
    mostrarComponenteUpload,
    openModalTitulosCedidos,
    setMostrarEnvioTitulos,
    verificaTitulosCedidos,
    excluirArquivoEnviado,
    onCloseModalCancelar,
    setOpenModalCancelar,
    liberarBtnProsseguir,
    documentosPendencias,
    voltarParaSimulacao,
    mostrarEnvioTitulos,
    loadingProsseguir,
    openModalCancelar,
    arquivosEnviados,
    tiposPendencias,
    uploadProgress,
    onUploadFiles,
    arquivosUI,
    setLoading,
    uploading,
    columns,
    loading,
    rows
  };
};

export default useUploadCessaoHook;
